/**
 * Sentry User Context Management
 * Tracks user information in Sentry for better error context
 */

import { setUser, setTag, addBreadcrumb } from './sentry-utils';
import { ERROR_LEVELS } from '../../constants/sentry-constants';
import type { AuthUser } from '@/hooks/use-auth';

// Mutex flag to prevent race conditions in setUserContext
let isUpdating = false;

/**
 * Update Sentry user context with user information
 * 
 * Uses a simple mutex to prevent race conditions when called concurrently
 * 
 * @param user - User object from authentication
 */
export function setUserContext(user: AuthUser | null): void {
  // Simple mutex: prevent concurrent updates
  if (isUpdating) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] User context update already in progress, skipping');
    }
    // Even if blocked by mutex, ensure tags are cleared if user is null
    if (!user) {
      setTag('accountId', 'guest');
    }
    return;
  }

  try {
    isUpdating = true;

    if (!user) {
      // Clear user context on logout
      setUser(null);
      setTag('accountId', 'guest');
      return;
    }

    // Set user context in Sentry
    setUser({
      id: user.id,
      email: user.email,
      username: user.email, // Use email as username
      // Additional user data
      accountId: String(user.accountId),
      accountName: user.accountName,
      subDomain: user.subDomain,
      // Custom attributes
      ...(user.customAttributes || {}),
    });

    // Set custom tags for filtering
    setTag('accountId', String(user.accountId));
    if (user.accountName) {
      setTag('accountName', user.accountName);
    }
    if (user.subDomain) {
      setTag('subDomain', user.subDomain);
    }

    // Add breadcrumb for user context change
    addBreadcrumb({
      category: 'auth',
      message: user.id ? 'User context updated' : 'User context cleared',
      level: ERROR_LEVELS.INFO,
      data: {
        userId: user.id,
        email: user.email,
        accountId: user.accountId,
      },
    });
  } finally {
    isUpdating = false;
  }
}

/**
 * Clear user context on logout
 */
export function onUserLogout(): void {
  setUserContext(null);
  
  addBreadcrumb({
    category: 'auth',
    message: 'User logged out',
    level: ERROR_LEVELS.INFO,
  });
}
